// Store
import { createSlice } from "@reduxjs/toolkit";
import { customAsyncThunk } from "store/slice/slices.functions";

// Services
import { fetchPermissionsRefBundle, fetchPermissionsRef, PermissionsRef, RoleBundleResponse } from "services/clienthub";

// Types
import { RootState } from "app/store";
import { RequestInfo } from "store/slice/store.types";
import { isEmpty } from "lodash";

type TPermissionsSlice = {
    ref: {
        status: RequestInfo;
        data: PermissionsRef[];
    };
    bundle: {
        status: RequestInfo;
        data: RoleBundleResponse;
    };
};

const initialState: TPermissionsSlice = {
    ref: {
        status: "pristine",
        data: [],
    },
    bundle: {
        status: "pristine",
        data: {} as RoleBundleResponse,
    },
};

export const fetchPermissionsRefThunk = customAsyncThunk<PermissionsRef[], void>(
    "sysadmin/permissionsRef/fetch",
    async (_, { getState }) => {
        const {
            sysadmin: {
                permissions: {
                    ref: { data: permissionsData },
                },
            },
        } = getState();

        if (permissionsData.length) {
            return permissionsData;
        } else {
            const { data } = await fetchPermissionsRef();
            return data.roles;
        }
    }
);

export const fetchPermissionsRefBundleThunk = customAsyncThunk<RoleBundleResponse, void>(
    "sysadmin/permissionsBundleRef/fetch",
    async (_, { getState }) => {
        const {
            sysadmin: {
                permissions: {
                    bundle: { data: permissionsBundle },
                },
            },
        } = getState();

        if (!isEmpty(permissionsBundle)) {
            return permissionsBundle;
        } else {
            const { data } = await fetchPermissionsRefBundle();
            return data;
        }
    }
);

const permissionsSlice = createSlice({
    name: "sysadmin/permissionsRef",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Ref
        builder.addCase(fetchPermissionsRefThunk.pending, (state) => ({
            ...state,
            ref: {
                ...state.ref,
                status: "fetching",
            },
        }));
        builder.addCase(fetchPermissionsRefThunk.rejected, (state) => ({
            ...state,
            ref: {
                ...state.ref,
                status: "error",
            },
        }));
        builder.addCase(fetchPermissionsRefThunk.fulfilled, (state, action) => ({
            ...state,
            ref: {
                data: action.payload,
                status: "done",
            },
        }));
        // Bundle Ref
        builder.addCase(fetchPermissionsRefBundleThunk.pending, (state) => ({
            ...state,
            bundle: {
                ...state.bundle,
                status: "fetching",
            },
        }));
        builder.addCase(fetchPermissionsRefBundleThunk.rejected, (state) => ({
            ...state,
            bundle: {
                ...state.bundle,
                status: "error",
            },
        }));
        builder.addCase(fetchPermissionsRefBundleThunk.fulfilled, (state, action) => ({
            ...state,
            bundle: {
                data: action.payload,
                status: "done",
            },
        }));
    },
});

export const getPermissionsRef = (store: RootState) => store.sysadmin.permissions.ref;
export const getPermissionsRefBundle = (store: RootState) => store.sysadmin.permissions.bundle;

export default permissionsSlice.reducer;
